// Página de listagem de fornecedores
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { getFornecedores, createFornecedor, updateFornecedor, deleteFornecedor } from '../../../services/fornecedoresService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCNPJ, formatDate } from '../../../utils/formatters';
import type { Fornecedor } from '../../../types';

export function Fornecedores() {
  const { empresaSelecionada, contaId } = useEmpresa();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fornecedorSelecionado, setFornecedorSelecionado] = useState<Fornecedor | null>(null);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (empresaSelecionada) {
      carregarFornecedores();
    }
  }, [empresaSelecionada]);

  const carregarFornecedores = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getFornecedores(empresaSelecionada.id);
      setFornecedores(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar fornecedores');
      console.error('Erro ao carregar fornecedores:', err);
    } finally {
      setLoading(false);
    }
  };

  const fornecedoresFiltrados = fornecedores.filter(f =>
    f.nome.toLowerCase().includes(busca.toLowerCase()) ||
    f.cnpj.includes(busca) ||
    f.email.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (fornecedor?: Fornecedor) => {
    setFornecedorSelecionado(fornecedor || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setFornecedorSelecionado(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir fornecedores');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir este fornecedor?')) {
      return;
    }

    try {
      await deleteFornecedor(id);
      await carregarFornecedores();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir fornecedor');
      console.error('Erro ao excluir fornecedor:', err);
    }
  };

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Fornecedores' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar os fornecedores</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Fornecedores' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Fornecedores' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Fornecedores</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Fornecedor
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      <Card>
        <Input
          label="Buscar Fornecedor"
          placeholder="Nome, CNPJ ou email"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>
      
      <Card>
        <Table
          data={fornecedoresFiltrados}
          columns={[
            { key: 'nome', label: 'Nome' },
            { key: 'cnpj', label: 'CNPJ', render: (item) => formatCNPJ(item.cnpj) },
            { key: 'email', label: 'Email' },
            { key: 'telefone', label: 'Telefone' },
            { key: 'createdAt', label: 'Cadastrado em', render: (item) => formatDate(item.createdAt) },
            {
              key: 'actions',
              label: 'Ações',
              render: (item) => (
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button onClick={() => abrirModal(item)} className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      
      <Modal isOpen={isModalOpen} onClose={fecharModal} title={fornecedorSelecionado ? 'Editar Fornecedor' : 'Novo Fornecedor'} size="lg">
        <FornecedorForm 
          fornecedor={fornecedorSelecionado} 
          onClose={fecharModal}
          onSuccess={carregarFornecedores}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      </Modal>
    </div>
  );
}

function FornecedorForm({ 
  fornecedor, 
  onClose, 
  onSuccess,
  isSaving,
  setIsSaving
}: { 
  fornecedor: Fornecedor | null; 
  onClose: () => void;
  onSuccess: () => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
}) {
  const { empresaSelecionada, contaId } = useEmpresa();
  const [formData, setFormData] = useState({
    nome: fornecedor?.nome || '',
    cnpj: fornecedor?.cnpj || '',
    email: fornecedor?.email || '',
    telefone: fornecedor?.telefone || '',
    cep: fornecedor?.endereco.cep || '',
    logradouro: fornecedor?.endereco.logradouro || '',
    numero: fornecedor?.endereco.numero || '',
    complemento: fornecedor?.endereco.complemento || '',
    bairro: fornecedor?.endereco.bairro || '',
    cidade: fornecedor?.endereco.cidade || '',
    estado: fornecedor?.endereco.estado || '',
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaSelecionada || !contaId) {
      setError('Empresa não selecionada ou sem conta vinculada');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const fornecedorData = {
        nome: formData.nome,
        cnpj: formData.cnpj,
        email: formData.email,
        telefone: formData.telefone,
        endereco: {
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
      };

      if (fornecedor) {
        await updateFornecedor(fornecedor.id, fornecedorData);
      } else {
        await createFornecedor(fornecedorData, contaId, empresaSelecionada.id);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar fornecedor');
      console.error('Erro ao salvar fornecedor:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="CNPJ"
          value={formData.cnpj}
          onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Telefone"
          value={formData.telefone}
          onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="CEP"
          value={formData.cep}
          onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
          required
          disabled={isSaving}
        />
        <div className="md:col-span-2">
          <Input
            label="Logradouro"
            value={formData.logradouro}
            onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
            required
            disabled={isSaving}
          />
        </div>
        <Input
          label="Número"
          value={formData.numero}
          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Complemento"
          value={formData.complemento}
          onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
          disabled={isSaving}
        />
        <Input
          label="Bairro"
          value={formData.bairro}
          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Cidade"
          value={formData.cidade}
          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Estado"
          value={formData.estado}
          onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
          required
          disabled={isSaving}
        />
      </div>
      
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
